import { useRef, useState } from 'react';
import instrumentsData from '@/data/instruments/index.json';
import definitionsData from '@/data/definitions.json';
import { WelcomeTour, isTourDismissed } from './WelcomeTour';
import amldData from '@/data/acts/amld.json';
import amlrData from '@/data/acts/amlr.json';
import amlarData from '@/data/acts/amlar.json';
import tfrData from '@/data/acts/tfr.json';
import citedNormsData from '@/data/cited_norms.json';
import type { GlossaryEntry } from '@/types';
import { glossaryHashFor, mandateHashFor } from '@/hooks/useHashRoute';
import { GlossaryEntryView, expansionStats } from './GlossaryEntryView';
import styles from './AppHeader.module.css';

interface Props {
  instrumentCount: number;
  dataDate?: string;
  onGoHome?: () => void;
}

const INSTRUMENTS_LIST = Object.values(instrumentsData as Record<string, any>);
const MANDATE_COUNT = INSTRUMENTS_LIST.length;

// Package statistics derived from the data (single source of truth) so the
// About / changelog copy never drifts from the actual content.
const ACTS = [amldData, amlrData, amlarData, tfrData] as any[];
const ALL_ARTICLES = ACTS.flatMap((a) => (a.chapters || []).flatMap((c: any) => c.articles || []));
const ARTICLE_COUNT = ALL_ARTICLES.length;
const RECITAL_COUNT = ACTS.reduce((n, a) => n + (a.recitals?.length || 0), 0);
const ANNEX_COUNT = ACTS.reduce((n, a) => n + (a.annexes?.length || 0), 0);
const CROSSREF_COUNT = ALL_ARTICLES.reduce((n: number, a: any) => n + (a.crossRefs?.length || 0), 0);
const ARTICLES_WITH_CROSSREFS = ALL_ARTICLES.filter((a: any) => (a.crossRefs?.length || 0) > 0).length;
const CITED_ACTS_COUNT = Array.isArray(citedNormsData)
  ? citedNormsData.length
  : Object.keys(citedNormsData as Record<string, unknown>).length;

const GLOSSARY = (definitionsData as any).entries as GlossaryEntry[];
const GLOSSARY_LETTERS = new Set(GLOSSARY.map((e) => (e.term[0] || '').toUpperCase()));
const GLOSSARY_STATS = expansionStats(GLOSSARY);

const BUILD_NUMBER = import.meta.env.VITE_BUILD_NUMBER;
const VERSION = BUILD_NUMBER ? `v4.${String(BUILD_NUMBER).padStart(4, '0')}` : 'v4.dev';

const PRIMARY_ACTS = [
  { shortName: 'AMLD',  code: 'Directive (EU) 2024/1640',  fullName: '6th Anti-Money Laundering Directive', url: 'https://eur-lex.europa.eu/eli/dir/2024/1640/oj/eng', color: '#1D4ED8' },
  { shortName: 'AMLR',  code: 'Regulation (EU) 2024/1624', fullName: 'AML Substantive Regulation', url: 'https://eur-lex.europa.eu/eli/reg/2024/1624/oj/eng', color: '#0D9488' },
  { shortName: 'AMLAR', code: 'Regulation (EU) 2024/1620', fullName: 'AMLA Founding Regulation', url: 'https://eur-lex.europa.eu/eli/reg/2024/1620/oj/eng', color: '#7C3AED' },
  { shortName: 'TFR',   code: 'Regulation (EU) 2023/1113', fullName: 'Funds Transfer Regulation', url: 'https://eur-lex.europa.eu/eli/reg/2023/1113/oj/eng', color: '#C2410C' },
];

const STATUS_COLORS: Record<string, string> = {
  adopted: '#6D28D9', final_report: '#15803D', consultation_closed: '#1D4ED8',
  consultation_open: '#B45309', planned: '#475569',
};

// Days elapsed since the data date, rendered as a human phrase ("today", "yesterday", "N days ago").
function daysAgoPhrase(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  const today = new Date();
  const utcToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const utcData = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const days = Math.round((utcToday - utcData) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

export function AppHeader({ instrumentCount, dataDate, onGoHome }: Props) {
  const [showLegend, setShowLegend]   = useState(false);
  // First-visit guided tour (dismissable; re-openable from the Tour button).
  const [showTour, setShowTour] = useState(() => !isTourDismissed());
  const [showInfoMenu, setShowInfoMenu] = useState(false);
  const [showAbout, setShowAbout]     = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showNorms, setShowNorms]     = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showDisclaimers, setShowDisclaimers] = useState(false);
  const [showGlossary, setShowGlossary] = useState(false);
  const [glossQuery, setGlossQuery] = useState('');
  const [glossLetter, setGlossLetter] = useState('All');
  // Cascade expanded state: keys are `${entryId}:${cascadeLabel}`
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const entryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // focusedEntryId not used here (deep-link is handled by GlossaryReaderPage route gate),
  // but the variable is kept for API symmetry with the shared GlossaryEntryView component.
  const focusedEntryId: string | null = null;

  const toggleStep = (entryId: string, label: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      const key = `${entryId}:${label}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const isStepExpanded = (entryId: string, label: string) =>
    expandedSteps.has(`${entryId}:${label}`);

  const glossFiltered = GLOSSARY.filter((e) => {
    if (glossLetter !== 'All' && (e.term[0] || '').toUpperCase() !== glossLetter) return false;
    const q = glossQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      e.term.toLowerCase().includes(q) ||
      (e.acronym || '').toLowerCase().includes(q) ||
      (e.gloss || '').toLowerCase().includes(q) ||
      (e.officialText || '').toLowerCase().includes(q)
    );
  });



  return (
    <>
      <header className={styles.header}>
        <div
          className={styles.flag}
          onClick={onGoHome}
          title="Back to main page"
          aria-label="Back to main page"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <span key={i} className={styles.star}>★</span>
          ))}
        </div>
        <button className={styles.versionBtn} onClick={() => setShowChangelog(true)}>{VERSION}</button>
        <div className={styles.center}>
          <h1
            className={styles.title}
            onClick={onGoHome}
            title="Back to main page"
          >AML Package Navigator – GIH AFC Officer Function</h1>
          <p className={styles.subtitle}>
            <button className={styles.subtitleLink} onClick={() => setShowNorms(true)}>
              1 Directive · 3 Regulations · {instrumentCount} RTS/ITS/GL (mandates)
            </button>

          </p>
        </div>
        <div className={styles.right} data-tour="header-links">
          {dataDate && <span className={styles.dataChip} title="Date of the last verified data refresh">Data as of <strong>{dataDate}</strong> · {daysAgoPhrase(dataDate)}</span>}
          <div className={styles.infoMenu} onMouseLeave={() => setShowInfoMenu(false)}>
            <button className={styles.headerLink} onClick={() => setShowInfoMenu(v => !v)} aria-haspopup="menu" aria-expanded={showInfoMenu}>Info ▾</button>
            {showInfoMenu && (
              <div className={styles.infoMenuList} role="menu">
                <button role="menuitem" className={styles.infoMenuItem} onClick={() => { setShowInfoMenu(false); setShowAbout(true); }}>About</button>
                <button role="menuitem" className={styles.infoMenuItem} onClick={() => { setShowInfoMenu(false); setShowFeatures(true); }}>Key features</button>
                <button role="menuitem" className={styles.infoMenuItem} onClick={() => { setShowInfoMenu(false); setShowDisclaimers(true); }}>Disclaimers</button>
                <button role="menuitem" className={styles.infoMenuItem} onClick={() => { setShowInfoMenu(false); setShowChangelog(true); }}>What's new ({VERSION})</button>
              </div>
            )}
          </div>
          <button className={styles.legendBtn} onClick={() => setShowTour(true)} title="Guided tour" aria-label="Open guided tour">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <polygon points="15.5,8.5 13.5,13.5 8.5,15.5 10.5,10.5" fill="currentColor" stroke="none" />
            </svg>
            Tour
          </button>
          <button className={styles.legendBtn} data-tour="legend" onClick={() => setShowLegend(true)} title="Legend" aria-label="Open legend modal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <circle cx="3.5" cy="6" r="1.5" fill="currentColor" stroke="none" />
              <circle cx="3.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
              <circle cx="3.5" cy="18" r="1.5" fill="currentColor" stroke="none" />
            </svg>
            Legend
          </button>
          <button className={styles.glossaryBtn} data-tour="glossary" onClick={() => setShowGlossary(true)} title="Glossary" aria-label="Open glossary modal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2z" />
              <line x1="8" y1="7" x2="15" y2="7" />
              <line x1="8" y1="11" x2="15" y2="11" />
            </svg>
            Glossary
          </button>
          <a href="https://www.amla.europa.eu/policy/regulatory-instruments_en" target="_blank" rel="noopener noreferrer" className={styles.amlaBtn}>
            🏛 AMLA ↗
          </a>
        </div>
      </header>

      <WelcomeTour open={showTour} onClose={() => setShowTour(false)}
        counts={{ mandates: MANDATE_COUNT, articles: ARTICLE_COUNT, recitals: RECITAL_COUNT, annexes: ANNEX_COUNT, glossary: GLOSSARY.length }} />

      {/* Legend modal */}
      {showLegend && (
        <div className={styles.modalOverlay} onClick={() => setShowLegend(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Legend</h2>
              <button className={styles.modalClose} onClick={() => setShowLegend(false)} aria-label="Close modal">✕</button>
            </div>
            <div className={styles.modalBody}>
              <h3>Act colors</h3>
              <div className={styles.legendRow}><span className={styles.dot} style={{background:'#1D4ED8'}} /> <strong>AMLD</strong> — Directive (EU) 2024/1640</div>
              <div className={styles.legendRow}><span className={styles.dot} style={{background:'#0D9488'}} /> <strong>AMLR</strong> — Regulation (EU) 2024/1624</div>
              <div className={styles.legendRow}><span className={styles.dot} style={{background:'#7C3AED'}} /> <strong>AMLAR</strong> — Regulation (EU) 2024/1620</div>
              <div className={styles.legendRow}><span className={styles.dot} style={{background:'#C2410C'}} /> <strong>TFR</strong> — Regulation (EU) 2023/1113</div>
              <h3>Article header indicators</h3>
              <div className={styles.legendRow}><span className={styles.dotRow}><span className={styles.dot} style={{background:'#1D4ED8'}} /><span className={styles.dot} style={{background:'#0D9488'}} /></span> Colored dots = cross-references to other acts</div>
              <div className={styles.legendRow}><span className={styles.mandatePill}>RTS</span> Regulatory Technical Standard mandate linked</div>
              <div className={styles.legendRow}><span className={styles.mandatePill}>ITS</span> Implementing Technical Standard mandate linked</div>
              <div className={styles.legendRow}><span className={styles.mandatePill}>GL</span> Guidelines mandate linked</div>
              <h3>Mandate status</h3>
              <div className={styles.legendRow}><span className={styles.statusBadge} style={{color:'#6D28D9',borderColor:'#6D28D9'}}>A</span> Adopted</div>
              <div className={styles.legendRow}><span className={styles.statusBadge} style={{color:'#15803D',borderColor:'#15803D'}}>FR</span> Final Report</div>
              <div className={styles.legendRow}><span className={styles.statusBadge} style={{color:'#1D4ED8',borderColor:'#1D4ED8'}}>CC</span> Consultation Closed</div>
              <div className={styles.legendRow}><span className={styles.statusBadge} style={{color:'#B45309',borderColor:'#B45309'}}>OC</span> Open Consultation</div>
              <div className={styles.legendRow}><span className={styles.statusBadge} style={{color:'#475569',borderColor:'#475569'}}>P</span> Planned</div>
              <h3>Content integrity badges</h3>
              <div className={styles.legendRow} style={{flexDirection:'column',alignItems:'flex-start',gap:6}}>
                <div><span style={{background:'#F0FDF4',border:'1px solid #BBF7D0',color:'#166534',fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:4}}>✓ Verbatim</span> — Text fetched verbatim from the official EUR-Lex source and integrity-verified via SHA-256 checksum.</div>
                <div><span style={{background:'#FEFCE8',border:'1px solid #FDE68A',color:'#854D0E',fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:4}}>⚠ Unverified</span> — Text not yet integrity-verified against the official source.</div>
                <div style={{fontSize:12,color:'#6B7280',marginTop:4,lineHeight:1.5}}>
                  <strong>Important:</strong> these badges reflect <em>automated checks only</em> — not human review. The checksum confirms the text has not changed since it was fetched from EUR-Lex, but does not guarantee completeness or visual fidelity to the Official Journal. Always verify against the <a href="https://eur-lex.europa.eu" target="_blank" rel="noopener noreferrer" className={styles.modalLink}>official EUR-Lex source</a> before relying on this text for legal or compliance purposes. Report any discrepancy to the tool administrator.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Glossary modal */}
      {showGlossary && (
        <div className={styles.modalOverlay} onClick={() => setShowGlossary(false)}>
          <div className={`${styles.modal} ${styles.glossModal}`} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Glossary <span className={styles.glossSub}>· definitions &amp; acronyms</span></h2>
              <span className={styles.glossCount}>{glossFiltered.length} of {GLOSSARY.length}</span>
              <a
                className={styles.glossOpenAllTab}
                href={glossaryHashFor()}
                target="_blank"
                rel="noopener noreferrer"
                title="Open the whole glossary in a new tab"
                aria-label="Open glossary in a new tab"
              >↗</a>
              <button className={styles.modalClose} onClick={() => setShowGlossary(false)} aria-label="Close modal">✕</button>
            </div>
            <div className={styles.glossTools}>
              <input
                className={styles.glossSearch}
                placeholder="Search term, acronym or definition…"
                value={glossQuery}
                onChange={e => setGlossQuery(e.target.value)}
                autoFocus
              />
              <div className={styles.azRow}>
                <button
                  className={`${styles.az} ${styles.azAll} ${glossLetter === 'All' ? styles.azActive : ''}`}
                  onClick={() => setGlossLetter('All')}
                >All</button>
                {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(L => {
                  const has = GLOSSARY_LETTERS.has(L);
                  return (
                    <button
                      key={L}
                      className={`${styles.az} ${!has ? styles.azOff : ''} ${glossLetter === L ? styles.azActive : ''}`}
                      disabled={!has}
                      onClick={() => has && setGlossLetter(L)}
                    >{L}</button>
                  );
                })}
              </div>
            </div>
            <div className={styles.glossInfoBar}>
              <span className={styles.glossInfoStat}>
                <strong>{GLOSSARY.length}</strong> entries
              </span>
              {GLOSSARY_STATS.entriesWithExp > 0 && (
                <>
                  <span className={styles.glossInfoSep}>·</span>
                  <span className={styles.glossInfoStat}>
                    <strong>{GLOSSARY_STATS.entriesWithExp}</strong> with nested-reference notes ({GLOSSARY_STATS.totalVerbatim} verbatim verified via EUR-Lex, up to {GLOSSARY_STATS.maxDepthSeen} levels deep)
                  </span>
                </>
              )}
            </div>
            <div className={styles.glossList}>
              {glossFiltered.length === 0 && <div className={styles.glossEmpty}>No matching entries.</div>}
              {glossFiltered.map(e => (
                <GlossaryEntryView
                  key={e.id}
                  entry={e}
                  isStepExpanded={isStepExpanded}
                  toggleStep={toggleStep}
                  entryRef={(el) => { entryRefs.current[e.id] = el; }}
                  focused={focusedEntryId === e.id}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* About modal */}
      {showAbout && (
        <div className={styles.modalOverlay} onClick={() => setShowAbout(false)}>
          <div className={styles.modal} style={{maxWidth: 520}} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>About AML Navigator</h2>
              <button className={styles.modalClose} onClick={() => setShowAbout(false)} aria-label="Close modal">✕</button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.aboutText}>
                AML Navigator is a structured reader for the EU AML/CFT legislative package — bringing together AMLD, AMLR, AMLAR and TFR in one place, so that everything you need is one click away from wherever you are in the text.
              </p>
              <p className={styles.aboutText}>
                The tool presents each regulation's articles, recitals and annexes alongside the technical standards (RTS/ITS/GL) that each article mandates, with full verbatim text from the Official Journal of the European Union.
              </p>
              <p className={styles.aboutText}>
                The integrated <strong>glossary</strong> resolves the classic "reference of the reference" problem in EU legal drafting: when a definition cites another act (and that one cites yet another), the chain is followed up to 5 levels of nesting, with every cited verbatim verified character-exact against EUR-Lex.
              </p>
              <div className={styles.disclaimer}>
                ⚠️ AML Navigator is a navigational tool only. Automated integrity checks (SHA-256 checksums, R1–R8 quality rules) verify content consistency but do not substitute verification against the official EUR-Lex source. Always confirm any provision before relying on it for legal or compliance purposes.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Key features modal */}
      {showFeatures && (
        <div className={styles.modalOverlay} onClick={() => setShowFeatures(false)}>
          <div className={styles.modal} style={{maxWidth: 600}} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Key features</h2>
              <button className={styles.modalClose} onClick={() => setShowFeatures(false)} aria-label="Close modal">✕</button>
            </div>
            <div className={styles.modalBody}>
              <p className={styles.featuresIntro}>
                The EU AML/CFT package is four interconnected acts that constantly reference each other, delegate to technical standards, and define terms used elsewhere. AML Navigator is built around a single idea: <strong>every element you need should be one click away, wherever you are in the text.</strong>
              </p>

              <h3>Finding your way in</h3>
              <ul className={styles.featureList}>
                <li>Four acts displayed <strong>side by side</strong>, so you can read corresponding provisions in parallel without switching documents</li>
                <li><strong>Main Acts / Mandates</strong>: switch between the four-act reading view and a <strong>mandates matrix</strong> — all {MANDATE_COUNT} RTS/ITS/GL laid out by status and act, filterable by type, status and search</li>
                <li><strong>Focus On</strong>: hide any act column to concentrate on what matters — works in both the acts view and the mandates matrix</li>
                <li><strong>Full-text search</strong> across all {ARTICLE_COUNT} articles, with filters by mandate type and status</li>
                <li><strong>Search by meaning</strong> (the <strong>Meaning</strong> toggle): describe what you need in plain words and get the closest verbatim passages from the acts, recitals, annexes, definitions and draft RTS/ITS/GL — ranked by a small language model that runs inside your browser, so nothing leaves your device</li>
                <li><strong>Clickable act badges</strong>: each column header opens an info card placing AMLD, AMLR, AMLAR and TFR within the EU AML/CFT package, with official sources</li>
              </ul>

              <h3>Following the connections</h3>
              <ul className={styles.featureList}>
                <li><strong>Cross-references</strong> between articles are shown as coloured dots; clicking one scrolls directly to the referenced provision — even across different acts</li>
                <li>Every article shows which <strong>RTS/ITS/GL it mandates</strong>, with status tracking across the AMLA 2026–2028 work programme — each mandate opens in full detail in a new tab</li>
                <li><strong>Referenced legislation</strong>: each article and recital lists the EU acts it cites, with direct links to EUR-Lex</li>
              </ul>

              <h3>Working with the mandates <span className={styles.featureBadge}>new</span></h3>
              <ul className={styles.featureList}>
                <li><strong>{MANDATE_COUNT} RTS/ITS/GL mandates</strong> from the AMLA 2026–2028 work programme, each with a <strong>two-level reader</strong>: a mandate page (summary, recitals, articles, annexes) and the full article reader with side-by-side comparison</li>
                <li><strong>Verbatim mandate content</strong> captured from the official AMLA consultation papers and final reports — articles, recitals (Whereas) and annexes — with references shown as links where the template lives in a separate document</li>
                <li><strong>Three planning dates per mandate</strong>: the statutory deadline (from the base article), the AMLA consultation date and the expected final-draft date (Annex XI), each shown with its own source</li>
                <li><strong>AI summaries</strong> per mandate and per article, based on the fetched verbatim text and shown with a visible disclaimer</li>
              </ul>

              <h3>Reading in depth</h3>
              <ul className={styles.featureList}>
                <li><strong>Full verbatim text</strong> from the Official Journal ({ARTICLE_COUNT} articles · {RECITAL_COUNT} recitals · {ANNEX_COUNT} annexes), rendered with full hierarchical structure — definitions, lettered points, roman sub-points — exactly as in the OJ. SHA-256 integrity verification on every article.</li>
                <li><strong>Recitals grouped by theme</strong>, with summaries — so you can read the legislative intent without scrolling through 89 or 175 individual paragraphs</li>
                <li>Every reader view — articles, recitals, annexes — <strong>opens in a dedicated tab</strong> so you keep your place in the main view</li>
              </ul>

              <h3>Glossary with multi-level nested references <span className={styles.featureBadge}>new</span></h3>
              <ul className={styles.featureList}>
                <li><strong>{GLOSSARY.length} terms and acronyms</strong> defined in the package, each with both the verbatim Official Journal text and an AI-generated plain-language gloss</li>
                <li><strong>Nested-reference resolution up to 5 levels deep</strong>: when a definition cites another EU act (e.g. "credit transfer as defined in PSD2 Art. 4(24)"), the cited definition is shown inline as a footnote — and if it cites further acts in turn, those are shown too, recursively. This solves the classic "reference of the reference of the reference" problem in EU law.</li>
                <li><strong>All nested verbatims verified against EUR-Lex</strong>: every quoted text is character-exact from the official source, with provenance metadata (source URL, fetch date, owner approval) tracked per entry</li>
                <li><strong>Cross-term links</strong>: when a definition uses another term defined elsewhere in the glossary, the term is underlined and one click opens that entry</li>
                <li><strong>Establishing source for organisation acronyms</strong>: for institutional acronyms (AMLA, EBA, ECB…), the founding regulation/directive is shown with a direct link to EUR-Lex</li>
                <li><strong>Repealed-norm handling</strong>: when the cited act has been repealed (e.g. PSD1 by PSD2), the entry surfaces both the textual reference (historical) and where it reads today (current), so legal audits and current application are both possible</li>
                <li><strong>Open the whole glossary in a new tab</strong> via the ↗ icon in the modal toolbar — bookmarkable URL (<code>#/glossary</code>)</li>
              </ul>

              <h3>Planning and exporting</h3>
              <ul className={styles.featureList}>
                <li><strong>Timeline</strong> of consultation deadlines, public hearings, statutory deadlines and framework milestones — opens at today, with same-date statutory deadlines grouped into one expandable entry</li>
                <li>Export mandates as <strong>CSV/Excel</strong>; export deadlines as a <strong>calendar file (.ics)</strong> for Outlook and Teams</li>
              </ul>

              <div className={styles.disclaimer}>
                ⚠️ AML Navigator is a navigational tool only. Automated integrity checks (SHA-256 checksums, R1–R8 quality rules) verify content consistency but do not substitute verification against the official EUR-Lex source. Always confirm any provision before relying on it for legal or compliance purposes.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Package norms modal */}
      {showNorms && (
        <div className={styles.modalOverlay} onClick={() => setShowNorms(false)}>
          <div className={styles.modal} style={{maxWidth: 580}} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>The AML/CFT package</h2>
              <button className={styles.modalClose} onClick={() => setShowNorms(false)} aria-label="Close modal">✕</button>
            </div>
            <div className={styles.modalBody}>
              <h3>Primary legislation</h3>
              <div className={styles.normsList}>
                {PRIMARY_ACTS.map(a => (
                  <a key={a.code} href={a.url} target="_blank" rel="noopener noreferrer" className={styles.normItem}>
                    <span className={styles.normShort} style={{color: a.color}}>{a.shortName}</span>
                    <span className={styles.normCode}>{a.code} — {a.fullName}</span>
                  </a>
                ))}
              </div>
              <h3>Technical standards (RTS/ITS/GL) — {MANDATE_COUNT} mandates</h3>
              <div className={styles.normsList}>
                {INSTRUMENTS_LIST.map((inst: any) => (
                  <button
                    key={inst.id}
                    type="button"
                    className={`${styles.normItem} ${styles.normItemBtn}`}
                    onClick={() => { window.open(mandateHashFor(inst.id), '_blank', 'noopener'); setShowNorms(false); }}
                    title={`Open ${inst.legalBasis} in the instrument reader`}
                  >
                    <span className={styles.normShort} style={{color: STATUS_COLORS[inst.status] ?? '#475569', minWidth: 40}}>
                      {inst.type}
                    </span>
                    <span className={styles.normCode}>
                      <span style={{color:'#333', fontWeight:600}}>{inst.legalBasis}</span>
                      {' — '}{inst.title}
                    </span>
                    <span className={styles.normStatus} style={{color: STATUS_COLORS[inst.status] ?? '#475569'}}>
                      {inst.statusLabel}
                    </span>
                    <span className={styles.normOpen} aria-hidden="true">↗</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Changelog modal */}
      {showChangelog && (
        <div className={styles.modalOverlay} onClick={() => setShowChangelog(false)}>
          <div className={styles.modal} style={{maxWidth: 560}} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Changelog</h2>
              <button className={styles.modalClose} onClick={() => setShowChangelog(false)} aria-label="Close modal">✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.changelogEntry}>
                <div className={styles.changelogVersion}>v4.0 — October 2026</div>
                <div className={styles.changelogLabel}>Search by meaning</div>
                <ul className={styles.changelogList}>
                  <li>New <strong>Meaning</strong> toggle in the search box: describe what you need in your own words ("when can simplified due diligence be applied") and get the closest passages across the four acts, their recitals and annexes, the glossary definitions and every draft RTS/ITS/GL — ranked by meaning, not by keywords</li>
                  <li>Runs <strong>entirely inside your browser</strong>: a small open language model (Snowflake arctic-embed-xs) and a pre-computed index of 3,389 passages are downloaded once (≈40 MB, then cached); no query ever leaves your device, no external service is called</li>
                  <li>Every hit is a <strong>verbatim passage</strong> with its reference — click opens it in the column or reader, ↗ opens the reader in a new tab; keyword search is unchanged and remains the default</li>
                  <li>Nine Final Reports imported with the official text (image-verified), 25/25 mandates on the latest AMLA document; five guided tours (48 steps) with a topic navigator; immediacy pass (view tabs, Info menu, Read full text above the summary, open-articles navigator, Today in the Timeline); preview deployments</li>
                </ul>
              </div>
              <div className={styles.changelogEntry}>
                <div className={styles.changelogVersion}>v3.0 — June 2026</div>
                <div className={styles.changelogLabel}>Mandate viewers consolidation</div>
                <ul className={styles.changelogList}>
                  <li>Mandate readers consolidated into one consistent two-column surface — every RTS/ITS/GL opens the same reader (summary, recitals, articles, annexes, accompanying documents and official-source links), with the article reader and side-by-side comparison opening in their own tab</li>
                  <li>Two-set guidelines (e.g. <strong>EBA/GL/2024/14 &amp; EBA/GL/2024/15</strong>) now render as parallel guideline columns, each article opening its own dedicated reader</li>
                  <li>Consultation feedback captured verbatim from the official EBA/AMLA Final Reports — the full comment-by-comment table (summary of responses, EBA analysis, amendments made) reproduced in the reader</li>
                  <li>Annex templates (e.g. FIU.net exchange forms) reproduced verbatim as structured field tables</li>
                  <li>Cost-benefit analysis / impact-assessment sections wired in verbatim alongside each mandate</li>
                  <li>Glossary extended with <strong>mandate-level definitions</strong> sourced verbatim from the guidelines, each badged with its EBA/AMLA source</li>
                  <li>Reader robustness — Print on every surface, in-article links no longer collapse the article, and corrected official-source links</li>
                </ul>
              </div>
              <div className={styles.changelogEntry}>
                <div className={styles.changelogVersion}>v2.0 — June 2026</div>
                <div className={styles.changelogLabel}>Mandates release</div>
                <ul className={styles.changelogList}>
                  <li>New <strong>Mandates view</strong> — a status × act matrix of all {MANDATE_COUNT} RTS/ITS/GL mandates, switchable from the new "Main Acts / Mandates" control, with Type/Status/search filters</li>
                  <li>New <strong>two-level mandate reader</strong> — mandate page (summary, recitals, articles, annexes) with the article reader and side-by-side comparison, opening in its own tab</li>
                  <li>Mandate data fully built out — per-article AI summaries, {CROSSREF_COUNT} cross-references, recitals (Whereas) and annexes captured verbatim from the official AMLA documents</li>
                  <li><strong>Planned-mandate scheduling</strong> — statutory deadline (from the base article) shown alongside the AMLA Annex XI consultation and final-draft dates, each with its source</li>
                  <li>Timeline upgrades — opens at today, and same-date statutory deadlines collapse into one expandable card</li>
                  <li>Clickable act badges — each opens an info card placing AMLD, AMLR, AMLAR and TFR within the EU AML/CFT package</li>
                  <li>Real mandate-level summaries with a visible AI disclaimer</li>
                </ul>
              </div>
                            <div className={styles.changelogEntry}>
                <div className={styles.changelogVersion}>v1.0 — May 2026</div>
                <div className={styles.changelogLabel}>Initial release</div>
                <ul className={styles.changelogList}>
                  <li>Four-column parallel view — AMLD, AMLR, AMLAR, TFR ({ARTICLE_COUNT} articles · {RECITAL_COUNT} recitals · {ANNEX_COUNT} annexes)</li>
                  <li>Full verbatim text from EUR-Lex with SHA-256 integrity verification (R1–R8 quality checks)</li>
                  <li>{MANDATE_COUNT} RTS/ITS/GL mandates with real-time status tracking (AMLA SPD 2026-2028)</li>
                  <li>{CROSSREF_COUNT} cross-references across {ARTICLES_WITH_CROSSREFS} articles with scroll-to navigation</li>
                  <li>Recitals organised in thematic groups with AI-assisted summaries</li>
                  <li>Referenced Legislation sidebar — {CITED_ACTS_COUNT} EU acts cited across the package</li>
                  <li>Full-text search, filters, Timeline view, CSV/ICS export</li>
                  <li>Structured article reader with hierarchical rendering of all EU list levels</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Disclaimers modal */}
      {showDisclaimers && (
        <div className={styles.modalOverlay} onClick={() => setShowDisclaimers(false)}>
          <div className={styles.modal} style={{maxWidth: 580}} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Disclaimers</h2>
              <button className={styles.modalClose} onClick={() => setShowDisclaimers(false)} aria-label="Close modal">✕</button>
            </div>
            <div className={styles.modalBody}>

              <p className={styles.aboutText} style={{fontStyle:'italic', color:'var(--text-muted)', marginBottom: 16}}>
                By using AML Navigator, you acknowledge and accept the terms set out below.
              </p>

              <h3>1. Navigational tool — not legal advice</h3>
              <p className={styles.aboutText}>
                AML Navigator is an internal navigational aid designed to support orientation within the EU AML/CFT legislative package. It does not constitute legal advice, regulatory guidance, compliance advice, or an authoritative interpretation of any legislative provision. No reliance should be placed on the content of this tool for legal, compliance, supervisory, or regulatory purposes without independent verification against official sources.
              </p>

              <h3>2. Official sources prevail</h3>
              <p className={styles.aboutText}>
                The sole authoritative text of all EU legislative acts is that published in the Official Journal of the European Union, accessible via EUR-Lex (<a href="https://eur-lex.europa.eu" target="_blank" rel="noopener noreferrer" className={styles.modalLink}>eur-lex.europa.eu</a>). In the event of any discrepancy — however arising — between the text displayed in AML Navigator and the official EUR-Lex publication, the EUR-Lex version prevails in all respects and without exception.
              </p>

              <h3>3. Automated integrity checks — limitation</h3>
              <p className={styles.aboutText}>
                Article texts are extracted verbatim from EUR-Lex and subject to automated integrity checks (SHA-256 checksums, R1–R8 quality rules). These checks verify internal consistency and traceability to source but are automated processes. They do not constitute human editorial review, do not guarantee the absence of extraction, parsing or rendering errors, and should not be interpreted as a certification of legal accuracy.
              </p>

              <h3>4. AI-generated summaries</h3>
              <p className={styles.aboutText}>
                Summaries of articles and recitals are generated by artificial intelligence and are clearly labelled as such throughout the tool. They are provided solely for orientation purposes and may contain inaccuracies, omissions, or oversimplifications inherent to automated processing. AI-generated summaries must not be used as a substitute for reading the full official text, and no legal or compliance conclusion should be drawn from them.
              </p>

              <h3>5. Currency and completeness</h3>
              <p className={styles.aboutText}>
                The legislative texts and data displayed reflect EUR-Lex content as at the date shown in the header. EU law is subject to amendment, corrigenda, and judicial interpretation at any time. RTS/ITS/GL mandate status is based on the AMLA Single Programming Document 2026–2028 (Annex XI, February 2026) and updated periodically; it may not reflect the most recent developments. Users are responsible for verifying current status directly on the AMLA and EUR-Lex websites.
              </p>

              <h3>6. No warranty — limitation of liability</h3>
              <p className={styles.aboutText}>
                AML Navigator is provided "as is", without warranty of any kind, express or implied, including but not limited to warranties of accuracy, completeness, fitness for a particular purpose, or non-infringement. To the extent permitted by applicable law, the author accepts no liability for any loss, damage, or adverse consequence arising directly or indirectly from the use of, or reliance on, this tool.
              </p>

              <h3>7. Legislative texts — copyright and reuse</h3>
              <p className={styles.aboutText}>
                The legislative texts reproduced in AML Navigator are © European Union. EUR-Lex content is made available under the <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener noreferrer" className={styles.modalLink}>Creative Commons Attribution 4.0 International licence (CC BY 4.0)</a>, pursuant to <a href="https://eur-lex.europa.eu/eli/dec/2011/833/oj" target="_blank" rel="noopener noreferrer" className={styles.modalLink}>Commission Decision 2011/833/EU</a> on the reuse of Commission documents. Reuse is permitted provided the source is acknowledged and any modifications are indicated. AML Navigator reproduces legislative texts verbatim without modification; the source (EUR-Lex, Official Journal of the European Union) is acknowledged throughout the tool. The tool's structure, navigation logic, and editorial organisation are the intellectual work of the author and are not covered by the CC BY 4.0 licence.
              </p>

              <h3>8. Scope of use</h3>
              <p className={styles.aboutText}>
                AML Navigator is an internal tool developed within the GIH AFC Officer Function at Generali. It is not intended for external distribution, public use, or use by persons outside Generali Group without express authorisation.
              </p>

            </div>
          </div>
        </div>
      )}

    </>
  );
}
